import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HERO } from "../../data/content";
import { PineTree, LeafShape } from "./decor";
import { Postmark } from "./scrapbook";

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="inici"
      onMouseMove={onMove}
      className="paper-grain relative flex min-h-[100svh] items-center overflow-hidden pb-28 pt-28"
      data-testid="section-inici"
    >
      {/* decoracions flotants (a les vores, sense solapar el contingut) */}
      <PineTree className="absolute left-[5%] top-[16%] hidden h-20 w-12 text-sage/40 animate-sway sm:block" />
      <PineTree className="absolute right-[6%] top-[20%] hidden h-28 w-16 text-forest/20 animate-float lg:block" />
      <PineTree className="absolute bottom-[8%] left-[10%] hidden h-16 w-10 text-sage/30 animate-bob sm:block" />
      <LeafShape className="absolute right-[12%] bottom-[14%] hidden h-12 w-12 text-terracotta/50 animate-float sm:block" />
      <LeafShape className="absolute left-[3%] bottom-[36%] hidden h-9 w-9 text-sage/40 animate-bob lg:block" />

      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-5 md:grid-cols-2">
        <div className="order-2 text-center md:order-1 md:text-left">
          <div className="relative inline-block collage tone-cream p-7 md:p-9">
            <span className="inline-flex items-center gap-2 font-display text-2xl font-semibold lowercase tracking-tight text-sage-ink sm:text-3xl" data-testid="hero-kicker">
              <LeafShape className="h-7 w-7 text-terracotta" />
              {HERO.kicker}
            </span>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-forest sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {HERO.title}
            </h1>
            <p className="mx-auto mt-5 max-w-md font-body text-base leading-relaxed text-forest/80 md:mx-0" data-testid="hero-subtitle">
              {HERO.subtitle}
            </p>
            <div className="mt-6 flex justify-center md:justify-start">
              <Postmark className="border-sage/60 text-sage" rotate={-3}>
                Fet a Lleida
              </Postmark>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3 md:justify-start">
            <button
              onClick={() => scrollTo("espectacles")}
              className="rounded-full bg-sage px-7 py-3 font-display font-semibold text-paper transition-transform duration-200 hover:-translate-y-1 hover:bg-forest paper-shadow"
              data-testid="hero-cta-primary"
            >
              {HERO.ctaPrimary}
            </button>
            <button
              onClick={() => scrollTo("qui-som")}
              className="rounded-full border-2 border-forest/40 bg-paper/70 px-7 py-3 font-display font-semibold text-forest transition-colors duration-200 hover:border-forest hover:bg-forest/5"
              data-testid="hero-cta-secondary"
            >
              {HERO.ctaSecondary}
            </button>
          </div>
        </div>

        {/* logo enganxat directament al fons */}
        <motion.div
          className="order-1 flex justify-center md:order-2"
          style={{ perspective: 900 }}
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.img
            src="/logo-badge.png"
            alt="Logo de Karamiko: un mico dins d'un cercle de pins"
            style={{ rotateX: rx, rotateY: ry }}
            className="w-64 max-w-[80%] sm:w-80 lg:w-[26rem]"
            data-testid="hero-logo"
          />
        </motion.div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-6 hidden justify-center md:flex">
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="font-hand text-xl font-bold text-forest/50"
        >
          comença el camí ↓
        </motion.div>
      </div>
    </section>
  );
}
